import { z } from 'zod';
import { IsoTimestamp, QuestionId, QuizId, SchoolId, UserId } from './common.js';
import { GRADE_LEVEL_NAME_MAX } from './grade-level.js';
import { SUBJECT_NAME_MAX } from './subject.js';
import { Question } from './quiz.js';
export const QuestionBankStatus = z.enum(['active', 'archived']);
export const QuestionBankSource = z.enum(['aimsis', 'cbt']);
const QuestionBankDocumentId = QuestionId.regex(/^[A-Za-z0-9:_-]+$/);
const QuestionBankQuizId = QuizId.regex(/^[A-Za-z0-9:_-]+$/);
export const QuestionBankForkLineage = z.object({
    itemId: QuestionId,
    source: QuestionBankSource,
    sourceVersion: z.number().int().positive(),
    sourceRevision: z.string().min(1).max(128).optional(),
});
export const QuestionBankItem = z.object({
    id: QuestionId,
    schoolId: SchoolId,
    ownerId: UserId,
    source: QuestionBankSource,
    sourceId: z.string().max(128).optional(),
    sourceCode: z.string().trim().min(1).max(128).optional(),
    sourceVersion: z.number().int().positive().default(1),
    sourceRevision: z.string().min(1).max(128).optional(),
    forkedFrom: QuestionBankForkLineage.optional(),
    subject: z.string().max(SUBJECT_NAME_MAX).optional(),
    subjectId: z.number().int().positive().optional(),
    gradeLevel: z.string().max(GRADE_LEVEL_NAME_MAX).optional(),
    classTypeId: z.number().int().positive().optional(),
    classType: z.string().max(100).optional(),
    major: z.string().max(100).optional(),
    competency: z.string().max(200).optional(),
    capaianId: z.number().int().positive().optional(),
    capaianLabel: z.string().max(200).optional(),
    tujuanId: z.number().int().positive().optional(),
    tujuanLabel: z.string().max(200).optional(),
    status: QuestionBankStatus.default('active'),
    question: Question,
    /** Unique bank-side group identity. `question.stimulusGroupId` is only the
     * author-typed display label — two imports or clones may share it — so group
     * membership is keyed on this field when present. */
    groupRef: z.string().min(1).max(64).optional(),
    /** The row's position in its source workbook (imports only) — the authoring
     * order that `createdAt` cannot express inside one import batch. */
    sourceRow: z.number().int().positive().optional(),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
    archivedAt: IsoTimestamp.optional(),
    createdBy: UserId.optional(),
    updatedBy: UserId.optional(),
});
export const QuestionBankUsageFilter = z.enum(['used', 'unused']);
export const QuestionBankMediaFilter = z.enum(['with', 'without']);
/** A date-range bound as an ISO-8601 instant (the web sends local-midnight UTC). */
const QuestionBankDateBound = z.string().trim().datetime({ offset: true });
export const QuestionBankListQuery = z.object({
    q: z.string().trim().max(100).optional(),
    subject: z.string().trim().max(SUBJECT_NAME_MAX).optional(),
    gradeLevel: z.string().trim().max(GRADE_LEVEL_NAME_MAX).optional(),
    source: QuestionBankSource.optional(),
    type: z.enum(['mcq', 'multipleAnswer', 'essay', 'trueFalse', 'shortAnswer', 'numeric', 'matching', 'ordering', 'labelContent']).optional(),
    status: QuestionBankStatus.default('active'),
    /** Filter by the item's creator uid (`createdBy`); items without the field are unattributed and never match. */
    authorId: z.string().trim().min(1).max(128).optional(),
    /** Question-level media availability; answer-option media is deliberately ignored. */
    media: QuestionBankMediaFilter.optional(),
    /** Quiz reuse relationship: any quiz question doc carrying `questionBankRef` = this item id. */
    usage: QuestionBankUsageFilter.optional(),
    createdFrom: QuestionBankDateBound.optional(),
    createdTo: QuestionBankDateBound.optional(),
    updatedFrom: QuestionBankDateBound.optional(),
    updatedTo: QuestionBankDateBound.optional(),
    after: QuestionBankDocumentId.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
}).superRefine((value, ctx) => {
    const ranges = [
        [value.createdFrom, value.createdTo, 'created'],
        [value.updatedFrom, value.updatedTo, 'updated'],
    ];
    for (const [from, to, label] of ranges) {
        if (from && to && Date.parse(from) > Date.parse(to)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${label}From must not be later than ${label}To`,
                path: [`${label}From`],
            });
        }
    }
});
/**
 * Curriculum references are an all-or-nothing group on every write path: a
 * partial set would persist the half-linked records the ticket forbids, and an
 * all-null PATCH is the explicit "clear/unchanged" signal for legacy rows.
 *
 * The IDs are declared with `z.coerce.number()` because the primary client is a
 * `<select>`, which submits the option value as a string; coercion accepts the
 * canonical wire form of a number without weakening the check — a non-numeric
 * value still fails `.int().positive()`.
 */
const CURRICULUM_FIELDS = ['subjectId', 'classTypeId', 'capaianId', 'capaianLabel', 'tujuanId', 'tujuanLabel'];
const CurriculumId = z.union([z.number(), z.string().regex(/^[0-9]+$/)])
    .pipe(z.coerce.number().int().positive());
const CURRICULUM_GROUP_MESSAGE = 'subjectId, classTypeId, capaianId, capaianLabel, tujuanId, and tujuanLabel must all be provided or all omitted';
function curriculumFieldsAreComplete(value) {
    const set = CURRICULUM_FIELDS.filter((field) => value[field] !== null && value[field] !== undefined);
    const cleared = CURRICULUM_FIELDS.filter((field) => value[field] === null);
    return (set.length === 0 && cleared.length === 0)
        || set.length === CURRICULUM_FIELDS.length
        || cleared.length === CURRICULUM_FIELDS.length;
}
export const CreateQuestionBankItemRequest = z.object({
    force: z.boolean().optional(),
    question: Question,
    subject: z.string().max(SUBJECT_NAME_MAX).optional(),
    subjectId: CurriculumId.optional(),
    gradeLevel: z.string().max(GRADE_LEVEL_NAME_MAX).optional(),
    classTypeId: CurriculumId.optional(),
    classType: z.string().max(100).optional(),
    major: z.string().max(100).optional(),
    competency: z.string().max(200).optional(),
    capaianId: CurriculumId.optional(),
    capaianLabel: z.string().max(200).optional(),
    tujuanId: CurriculumId.optional(),
    tujuanLabel: z.string().max(200).optional(),
    sourceCode: z.string().trim().min(1).max(128).optional(),
    sourceId: z.string().max(128).optional(),
}).refine(curriculumFieldsAreComplete, { message: CURRICULUM_GROUP_MESSAGE });
export const UpdateQuestionBankItemRequest = z.object({
    question: Question.optional(),
    subject: z.string().max(SUBJECT_NAME_MAX).nullable().optional(),
    subjectId: CurriculumId.nullable().optional(),
    gradeLevel: z.string().max(GRADE_LEVEL_NAME_MAX).nullable().optional(),
    classTypeId: CurriculumId.nullable().optional(),
    classType: z.string().max(100).nullable().optional(),
    major: z.string().max(100).nullable().optional(),
    competency: z.string().max(200).nullable().optional(),
    capaianId: CurriculumId.nullable().optional(),
    capaianLabel: z.string().max(200).nullable().optional(),
    tujuanId: CurriculumId.nullable().optional(),
    tujuanLabel: z.string().max(200).nullable().optional(),
    expectedVersion: z.number().int().positive(),
}).strict().refine((value) => value.question !== undefined
    || value.subject !== undefined
    || value.subjectId !== undefined
    || value.gradeLevel !== undefined
    || value.classTypeId !== undefined
    || value.classType !== undefined
    || value.major !== undefined
    || value.competency !== undefined
    || value.capaianId !== undefined
    || value.capaianLabel !== undefined
    || value.tujuanId !== undefined
    || value.tujuanLabel !== undefined, { message: 'at least one editable field is required' }).refine(curriculumFieldsAreComplete, { message: CURRICULUM_GROUP_MESSAGE });
export const CloneQuestionBankItemRequest = z.object({}).strict();
export const ChangeQuestionBankItemStatusRequest = z.object({
    expectedVersion: z.number().int().positive(),
}).strict();
export const QuestionBankIdParams = z.object({
    id: QuestionBankDocumentId,
});
export const AddQuestionBankItemToQuizRequest = z.object({
    bankItemIds: z.array(QuestionBankDocumentId).min(1).max(100),
    quizId: QuestionBankQuizId,
    requestId: z.string().uuid(),
}).strict();
/**
 * Bank-side group creation (AIMSIS-21209): link two or more existing records
 * under one generated `stimulusGroupId` and one shared stimulus. The stimulus
 * cap mirrors `Question.stimulus` (schemas/quiz.ts) so a stamped question can
 * never fail the stored-document schema.
 */
export const CreateQuestionBankGroupRequest = z.object({
    bankItemIds: z.array(QuestionBankDocumentId).min(2).max(100),
    stimulus: z.string().trim().min(1).max(20000),
}).strict();
export const QuestionImportMode = z.enum(['mixed', 'single']);
export const QuestionTypeForImport = z.enum(['mcq', 'multipleAnswer', 'essay', 'trueFalse', 'shortAnswer', 'numeric', 'matching', 'ordering', 'labelContent']);
/**
 * Every field a workbook column can resolve to: the alias table
 * (`HEADER_ALIASES` in `api/src/lib/question-bank-workbook.ts`) detects all of
 * these, and the mapping panel may assign any of them explicitly. `option`
 * marks a column as an answer choice, `ignore` drops it from the import
 * entirely, and `unmapped` is an EXPLICIT "do not assign a field" — distinct
 * from omitting the column, which lets the parser re-apply its own alias
 * detection. Without `unmapped` a reviewer cannot undo a wrong auto-detection:
 * the decision would travel as absence and be overwritten by the alias on the
 * next parse.
 */
export const QuestionBankImportField = z.enum([
    'code', 'type', 'content', 'subject', 'subjectId', 'gradeLevel', 'classTypeId', 'classType',
    'major', 'competency', 'capaianId', 'capaianLabel', 'tujuanId', 'tujuanLabel', 'status',
    'correct', 'explanation', 'duration', 'stimulusGroupId', 'stimulus', 'option', 'ignore', 'unmapped',
]);
export const QuestionBankImportColumnMapping = z.object({
    column: z.number().int().nonnegative().max(63),
    field: QuestionBankImportField,
});
/** Base64 length of the largest workbook the import accepts. The preview route's
 * body limit is derived from this, so the transport and the schema cannot drift
 * apart and reject a file the schema advertises as valid. */
export const MAX_WORKBOOK_BASE64_LENGTH = 8_000_000;
export const PreviewQuestionBankWorkbookRequest = z.object({
    fileName: z.string().trim().min(1).max(255),
    workbookBase64: z.string().min(1).max(MAX_WORKBOOK_BASE64_LENGTH),
    /** Mixed reads the type from each row's Type column; single applies
     * `singleQuestionType` to every row and does not require the column. */
    questionMode: QuestionImportMode.default('mixed'),
    singleQuestionType: QuestionTypeForImport.optional(),
    /** The reviewer's explicit column assignments, overriding alias detection. */
    fieldMapping: z.array(QuestionBankImportColumnMapping).max(64).optional(),
    /** 1-based sheet row the reviewer picked as the header, sent after a preview
     * reported `headerRowDetected: false` or offered `headerCandidates`. Column
     * mapping alone cannot repair a wrong header — the header decides which cells
     * are even read as fields — so the pick travels as its own input. */
    headerRow: z.number().int().positive().max(10_000).optional(),
}).strict();
export const ImportRowAssignment = z.object({
    rowNumber: z.number().int().positive(),
    type: QuestionTypeForImport,
});
export const ConfirmQuestionBankWorkbookRequest = z.object({
    digest: z.string().regex(/^[a-f0-9]{64}$/),
    /** Finalization stays blocked while any row is not `passed`. The author clears
     * the block either by fixing the workbook and previewing again, or by
     * explicitly skipping the remaining rows and importing only the passed ones. */
    skipInvalidRows: z.boolean().default(false),
    /** Types chosen during Preview & Review for rows with a Type fault — an empty
     * Type cell, or one the parser could not read. They are re-validated
     * server-side before the row is imported. */
    assignments: z.array(ImportRowAssignment).max(100).optional(),
}).strict();
export const QuestionBankImportIdParams = z.object({
    importId: z.string().uuid(),
});
//# sourceMappingURL=question-bank.js.map